// Book-name matcher for Bible search (arc docs/plans/bible-search, chunk 07.1).
//
// Probe-importable: imports only CANONICAL_BOOKS (bsb-types), ALIAS_MAP
// (bsb-parser, a pure module that itself imports only bsb-types), and the
// zero-import normalizeForSearch / osaDistance leaves. No React, no IDB, no
// @/ aliases, so scripts/probe-bible-book-search.mjs loads it under tsx.
//
// searchBooks ranks the 66 canonical books against a free-text query by
// name / bookTitle / alias, accent-folded, in strict tiers: exact > prefix >
// substring > bounded typo, then canonical order as the stable tiebreak. It is
// the data source for the Books category on the /bible search surface, where
// matches render as the same accordion + chapter grid as the main index.

import { CANONICAL_BOOKS, type CanonicalBookEntry } from '../bsb-types';
import { ALIAS_MAP } from '../bsb-parser';
import { normalizeForSearch } from '../search-normalize';
import { osaDistance, commonPrefixLength } from '../edit-distance';

// Length gates, mirroring the spirit of the verse engine's fuzzy thresholds.
// Prefix needs >= 2 chars and substring >= 3 so a 1-2 char query doesn't flood
// the list (every book name contains common short substrings). Fuzzy mirrors
// bsb-search-core: only queries >= 4 chars are typo-matched, threshold 1 for
// [4,7) and 2 for >= 7.
const PREFIX_MIN_LEN = 2;
const SUBSTRING_MIN_LEN = 3;
const FUZZY_MIN_LEN = 4;
const FUZZY_LONG_LEN = 7;

function fuzzyThreshold(len: number): number {
  return len >= FUZZY_LONG_LEN ? 2 : 1;
}

// slug -> the set of normalized candidate strings (canonical name + optional
// bookTitle + every alias), built once at module load. Aliases arrive
// pre-lowercased from bsb-parser; we still fold them through normalizeForSearch
// so the comparison space is byte-identical to the normalized query.
const CANDIDATES_BY_SLUG: ReadonlyMap<string, readonly string[]> = (() => {
  const acc = new Map<string, Set<string>>();
  const add = (slug: string, raw: string): void => {
    const norm = normalizeForSearch(raw);
    if (!norm) return;
    let set = acc.get(slug);
    if (!set) {
      set = new Set<string>();
      acc.set(slug, set);
    }
    set.add(norm);
  };
  for (const b of CANONICAL_BOOKS) {
    add(b.slug, b.name);
    if (b.bookTitle) add(b.slug, b.bookTitle);
  }
  for (const [alias, slug] of ALIAS_MAP) add(slug, alias);
  const out = new Map<string, readonly string[]>();
  for (const [slug, set] of acc) out.set(slug, [...set]);
  return out;
})();

// Match tiers, lower is better; TIER_NONE means no match.
const TIER_EXACT = 0;
const TIER_PREFIX = 1;
const TIER_SUBSTRING = 2;
const TIER_FUZZY = 3;
const TIER_NONE = 4;

interface BookMatch {
  tier: number;
  // Fuzzy-only quality signals (0 / -1 placeholders for the non-fuzzy tiers,
  // which rank purely by canonical order). dist asc, prefix desc, lenDelta asc:
  // the same order the verse engine uses so a typo prefers its intended word
  // ("jhon" -> "john", lenDelta 0) over an equidistant neighbor ("jon" -> Jonah,
  // lenDelta 1).
  dist: number;
  prefix: number;
  lenDelta: number;
}

// Best match any of a book's candidate strings achieves against q, or null.
function evaluateBook(candidates: readonly string[], q: string): BookMatch | null {
  const qlen = q.length;
  const canFuzzy = qlen >= FUZZY_MIN_LEN;
  const maxDist = canFuzzy ? fuzzyThreshold(qlen) : 0;
  let tier = TIER_NONE;
  let bestDist = Infinity;
  let bestPrefix = -1;
  let bestLenDelta = Infinity;
  for (const c of candidates) {
    if (c === q) return { tier: TIER_EXACT, dist: 0, prefix: 0, lenDelta: 0 };
    if (qlen >= PREFIX_MIN_LEN && c.startsWith(q)) {
      tier = Math.min(tier, TIER_PREFIX);
      continue;
    }
    if (qlen >= SUBSTRING_MIN_LEN && c.includes(q)) {
      tier = Math.min(tier, TIER_SUBSTRING);
      continue;
    }
    // OSA is at least the length difference, so skip candidates that can't be
    // in threshold before the O(len*len) DP.
    if (canFuzzy && Math.abs(c.length - qlen) <= maxDist) {
      const d = osaDistance(q, c);
      if (d <= maxDist) {
        tier = Math.min(tier, TIER_FUZZY);
        const prefix = commonPrefixLength(q, c);
        const lenDelta = Math.abs(c.length - qlen);
        if (
          d < bestDist ||
          (d === bestDist && prefix > bestPrefix) ||
          (d === bestDist && prefix === bestPrefix && lenDelta < bestLenDelta)
        ) {
          bestDist = d;
          bestPrefix = prefix;
          bestLenDelta = lenDelta;
        }
      }
    }
  }
  if (tier === TIER_NONE) return null;
  // The fuzzy quality signals only rank WITHIN the fuzzy tier; a book that
  // matched at prefix/substring carries placeholders that never get compared.
  if (tier === TIER_FUZZY) {
    return { tier, dist: bestDist, prefix: bestPrefix, lenDelta: bestLenDelta };
  }
  return { tier, dist: 0, prefix: 0, lenDelta: 0 };
}

/**
 * Rank the 66 canonical books against a free-text query. Returns at most
 * `limit` `CanonicalBookEntry` rows, best match first (tier, then within the
 * fuzzy tier by edit distance / shared prefix / length delta, then canonical
 * order). One row per book (candidates are folded per slug). Empty/whitespace
 * query or zero matches -> [].
 */
export function searchBooks(query: string, limit = 8): CanonicalBookEntry[] {
  const q = normalizeForSearch(query);
  if (!q) return [];
  const matched: { book: CanonicalBookEntry; m: BookMatch }[] = [];
  for (const book of CANONICAL_BOOKS) {
    const candidates = CANDIDATES_BY_SLUG.get(book.slug);
    if (!candidates) continue;
    const m = evaluateBook(candidates, q);
    if (!m) continue;
    matched.push({ book, m });
  }
  matched.sort((a, b) => {
    if (a.m.tier !== b.m.tier) return a.m.tier - b.m.tier;
    if (a.m.tier === TIER_FUZZY) {
      if (a.m.dist !== b.m.dist) return a.m.dist - b.m.dist;
      if (a.m.prefix !== b.m.prefix) return b.m.prefix - a.m.prefix;
      if (a.m.lenDelta !== b.m.lenDelta) return a.m.lenDelta - b.m.lenDelta;
    }
    return a.book.order - b.book.order;
  });
  const lim = limit > 0 ? limit : 8;
  return matched.slice(0, lim).map((entry) => entry.book);
}
